type SerializableValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | SerializableValue[]
    | { [key: string]: SerializableValue };


const remaining = (string: string, max: number) => (max - string.length) + " " + "characters remaining";

const safeSerialize = (
	obj: unknown,
	depth: number = 0,
	seen: WeakSet<object> = new WeakSet()
): SerializableValue => {
	if (obj == null) return obj;
	if (depth > 5) return "[Max depth]";
	if (typeof obj === "function") return `[Function ${obj.name || "anonymous"}]`;
	if (typeof obj !== "object") return obj as SerializableValue;
	if (seen.has(obj)) return "[Circular]";
	seen.add(obj);

	if (Array.isArray(obj)) return obj.map(i => safeSerialize(i, depth + 1, seen));

	const out: { [key: string]: SerializableValue } = {};
	for (const key of Object.keys(obj)) {
		try {
			out[key] = safeSerialize((obj as Record<string, unknown>)[key], depth + 1, seen);
		} catch (e) {
			out[key] = `[Unserializable: ${e && (e as Error).message ? (e as Error).message : String(e)}]`;
		}
	}
	return out;
};

export type SerializedError = {
	name: string;
	message: string;
	stack?: string;
	code?: string | number;
	type?: number | string;
	response?: SerializableValue;
	request?: SerializableValue;
	[key: string]: SerializableValue;
};

const serializeError = (err: unknown): SerializedError | null => {
	if (err == null) return null;

	try {
		if (typeof err === "string" || typeof err === "number" || typeof err === "boolean") {
			return { name: typeof err, message: String(err) };
		}

		if (err instanceof Error) {
			const out: SerializedError = {
				name: err.name,
				message: err.message,
				stack: err.stack
			};

			if ("code" in err && err.code) out.code = err.code as string | number;
			if ("status" in err && err.status) out.type = err.status as number | string;

			// Axios-like errors
			if ("response" in err && err.response) {
				const response = err.response as Record<string, unknown>;
				if ("status" in response && response.status) out.type = response.status as number;
				out.response = safeSerialize(err.response);
			}
			if ("request" in err && err.request) out.request = safeSerialize(err.request);

			// copy enumerable custom props
			Object.keys(err).forEach(k => {
				if (!["name", "message", "stack", "code", "status", "response", "request"].includes(k)) {
					out[k] = safeSerialize((err as unknown as Record<string, unknown>)[k]);
				}
			});

			return out;
		}

		// Generic object
		return safeSerialize(err) as SerializedError;
	} catch {
		return { name: "SerializationError", message: "Failed to serialize error", originalType: typeof err };
	}
};


export default { remaining, serializeError };